import { useCallback, useEffect, useState } from "react";
import { getPlayerInfo } from "../../utils/component-utils";


export function useLocalAudio() {
  const volume = window.APP.store.state?.preferences?.globalVoiceVolume;

  const updateLocalAudio = useCallback((value) => {
      APP.store.update({
        preferences: {
          globalVoiceVolume: value
        }
      });
  }, [])


  return [volume, updateLocalAudio];
}

export function useAllAvatarVolume(people, onMultiplierChanged) {
  const [multipliers, setMultipliers] = useState({});
  const [mutedStates, setMutedStates] = useState({});

  // Function to update the multiplier for a specific person
  const updatePersonMultiplier = useCallback((personId, gainMultiplier) => {
    const playerInfo = getPlayerInfo(personId);
    const controlsEl = playerInfo?.el.querySelector("[avatar-volume-controls]");
    const controls = controlsEl?.components["avatar-volume-controls"];

    if (!controls) return;

    setMultipliers(prev => ({
      ...prev,
      [personId]: gainMultiplier,
    }));
    controls.updateGainMultiplier(gainMultiplier, true);
    onMultiplierChanged && onMultiplierChanged(personId, gainMultiplier);
  }, [onMultiplierChanged]);

  // Function to update mute state for a specific person
  const updatePersonMuted = useCallback((personId, muted) => {
    const playerInfo = getPlayerInfo(personId);
    const controlsEl = playerInfo?.el.querySelector("[avatar-volume-controls]");
    const controls = controlsEl?.components["avatar-volume-controls"];

    if (!controls) return;

    setMutedStates(prev => ({
      ...prev,
      [personId]: muted,
    }));
    controls.updateLocalMuted(muted, true);
  }, []);

  useEffect(() => {
    people.forEach(person => {
      const playerInfo = getPlayerInfo(person.id);
      const controlsEl = playerInfo?.el.querySelector("[avatar-volume-controls]");
      const controls = controlsEl?.components["avatar-volume-controls"];

      if (controlsEl) {
        const gainMultiplier = APP.gainMultipliers.get(controls.audioEl);
        setMultipliers(prev => ({
          ...prev,
          [person.id]: gainMultiplier,
        }));

        const isMuted = APP.mutedState.has(controls.audioEl);
        setMutedStates(prev => ({
          ...prev,
          [person.id]: isMuted,
        }));

        controlsEl.addEventListener("gain_multiplier_updated", event => {
          const newMultiplier = event.detail.gainMultiplier;
          setMultipliers(prev => ({
            ...prev,
            [person.id]: newMultiplier,
          }));
        });

        controlsEl.addEventListener("local_muted_updated", event => {
          const muted = event.detail.muted;
          setMutedStates(prev => ({
            ...prev,
            [person.id]: muted,
          }));
        });
      }
    });

    return () => {
      people.forEach(person => {
        const playerInfo = getPlayerInfo(person.id);
        const controlsEl = playerInfo?.el.querySelector("[avatar-volume-controls]");

        // if (controlsEl) {
        //   controlsEl.removeEventListener("gain_multiplier_updated");
        //   controlsEl.removeEventListener("local_muted_updated");
        // }
      });
    };
  }, [people]);

  return { multipliers, updatePersonMultiplier, mutedStates, updatePersonMuted };
}


export default function useAvatarVolume(sessionId, onMultiplierChanged) {
  const playerInfo = getPlayerInfo(sessionId);
  const controlsEl = playerInfo?.el.querySelector("[avatar-volume-controls]");
  const controls = controlsEl?.components["avatar-volume-controls"];
  const [multiplier, setMultiplier] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const onMultiplierUpdated = useCallback(
    ({ detail: { gainMultiplier } }) => {
      setMultiplier(gainMultiplier);
      onMultiplierChanged && onMultiplierChanged(gainMultiplier);
    },
    [onMultiplierChanged]
  );

  const updateMultiplier = useCallback(
    gainMultiplier => {
      if (!controls) return;
      setMultiplier(gainMultiplier);
      controls.updateGainMultiplier(gainMultiplier, true);
    },
    [controls]
  );

  const onLocalMutedUpdated = useCallback(({ detail: { muted } }) => {
    setIsMuted(muted);
  }, []);

  const updateMuted = useCallback(
    muted => {
      if (!controls) return;
      setIsMuted(!!muted);
      controls.updateLocalMuted(!!muted, true);
    },
    [controls]
  );

  useEffect(() => {
    if (!controlsEl) return;
    controlsEl.addEventListener("gain_multiplier_updated", onMultiplierUpdated);
    controlsEl.addEventListener("local_muted_updated", onLocalMutedUpdated);

    const gainMultiplier = APP.gainMultipliers.get(controls.audioEl);
    onMultiplierUpdated({ detail: { gainMultiplier } });
    const isLocalMuted = APP.mutedState.has(controls.audioEl);
    setIsMuted(isLocalMuted);

    return () => {
      controlsEl.removeEventListener("gain_multiplier_updated", onMultiplierUpdated);
      controlsEl.removeEventListener("local_muted_updated", onLocalMutedUpdated);
    };
  }, [controls, controlsEl, onMultiplierUpdated, onLocalMutedUpdated]);

  return [multiplier, updateMultiplier, isMuted, updateMuted];
}
